import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  User,
  Calendar,
  MessageSquare,
  AlertCircle,
  Mail,
  Briefcase,
  RefreshCw,
  Inbox,
  Send,
  Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentInvitations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem('session_token');
  
  const [loading, setLoading] = useState(true);
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [activeTab, setActiveTab] = useState('received');
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      // Fetch received invitations
      const receivedRes = await fetch(`${API_URL}/api/agent-invitations/received`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (receivedRes.ok) {
        setReceivedInvitations(await receivedRes.json());
      }

      // Fetch sent invitations (if this user is also a trustee)
      const sentRes = await fetch(`${API_URL}/api/agent-invitations/sent`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sentRes.ok) {
        setSentInvitations(await sentRes.json());
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitation) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/agent-invitations/accept/${invitation.invitation_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`You are now managing ${invitation.building_name}!`);
        setShowDetailsDialog(false);
        fetchInvitations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Accept error:', error);
      toast.error('Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedInvitation) return;
    
    setProcessing(true);
    try {
      const url = declineReason 
        ? `${API_URL}/api/agent-invitations/decline/${selectedInvitation.invitation_id}?reason=${encodeURIComponent(declineReason)}`
        : `${API_URL}/api/agent-invitations/decline/${selectedInvitation.invitation_id}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Invitation declined');
        setShowDeclineDialog(false);
        setShowDetailsDialog(false);
        setDeclineReason('');
        fetchInvitations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to decline invitation');
      }
    } catch (error) {
      console.error('Decline error:', error);
      toast.error('Failed to decline invitation');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'accepted':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Accepted</Badge>;
      case 'declined':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Declined</Badge>;
      case 'expired':
        return <Badge className="bg-slate-100 text-slate-700">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingCount = receivedInvitations.filter(i => i.status === 'pending').length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agent-invitations-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/agent')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Building Invitations</h1>
                <p className="text-sm text-slate-500">Manage invitations from trustees</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {pendingCount > 0 && (
                <Badge className="bg-indigo-600 text-white">{pendingCount} pending</Badge>
              )}
              <Button variant="outline" size="sm" onClick={fetchInvitations}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="received" className="flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Received
              {pendingCount > 0 && (
                <Badge className="ml-1 bg-indigo-500 text-white text-xs">{pendingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Sent
            </TabsTrigger>
          </TabsList>

          {/* Received Invitations Tab */}
          <TabsContent value="received">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
              </div>
            ) : receivedInvitations.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No Invitations</h3>
                  <p className="text-slate-500">You haven't received any building management invitations yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {receivedInvitations.map(invitation => (
                  <Card 
                    key={invitation.invitation_id}
                    className={`hover:shadow-md transition-shadow ${invitation.status === 'pending' ? 'border-indigo-200 bg-indigo-50/30' : ''}`}
                    data-testid={`invitation-${invitation.invitation_id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            invitation.status === 'pending' ? 'bg-indigo-100' : 'bg-slate-100'
                          }`}>
                            <Building2 className={`w-6 h-6 ${invitation.status === 'pending' ? 'text-indigo-600' : 'text-slate-500'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{invitation.building_name}</h3>
                              {getStatusBadge(invitation.status)}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              {invitation.building_address}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                From: {invitation.invited_by_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(invitation.created_at)}
                              </span>
                            </div>
                            {invitation.message && (
                              <div className="mt-3 p-3 bg-white rounded-lg border">
                                <p className="text-sm text-slate-600 flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 mt-0.5 text-slate-400" />
                                  "{invitation.message}"
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {invitation.status === 'pending' && (
                            <>
                              <Button 
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleAccept(invitation)}
                                disabled={processing}
                                data-testid={`accept-${invitation.invitation_id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedInvitation(invitation);
                                  setShowDeclineDialog(true);
                                }}
                                data-testid={`decline-${invitation.invitation_id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Decline
                              </Button>
                            </>
                          )}
                          <Button 
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedInvitation(invitation);
                              setShowDetailsDialog(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sent Invitations Tab */}
          <TabsContent value="sent">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
              </div>
            ) : sentInvitations.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Send className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No Sent Invitations</h3>
                  <p className="text-slate-500">You haven't sent any invitations to managing agents.</p>
                  <Button className="mt-4" onClick={() => navigate('/trustee-hub')}>
                    Go to Trustee Hub
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sentInvitations.map(invitation => (
                  <Card key={invitation.invitation_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-900">{invitation.company_name}</h3>
                            <p className="text-sm text-slate-500">{invitation.agent_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(invitation.status)}
                          <span className="text-xs text-slate-400">{formatDate(invitation.created_at)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Invitation Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Invitation Details
            </DialogTitle>
          </DialogHeader>
          {selectedInvitation && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedInvitation.status)}
              </div>

              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Building</p>
                  <p className="font-semibold">{selectedInvitation.building_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Address</p>
                  <p>{selectedInvitation.building_address}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Invited by</p>
                  <p>{selectedInvitation.invited_by_name} ({selectedInvitation.invited_by_email})</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Received</p>
                  <p>{formatDate(selectedInvitation.created_at)}</p>
                </div>
              </div>

              {selectedInvitation.message && (
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <p className="text-sm font-medium text-indigo-700 mb-1">Message from Trustee:</p>
                  <p className="text-indigo-900">"{selectedInvitation.message}"</p>
                </div>
              )}

              {selectedInvitation.status === 'pending' && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Accepting this invitation will make you the managing agent for this building. You will be responsible for managing maintenance, documents, and communication with residents.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {selectedInvitation?.status === 'pending' && (
              <>
                <Button 
                  variant="outline"
                  className="text-red-600"
                  onClick={() => setShowDeclineDialog(true)}
                  disabled={processing}
                >
                  Decline
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleAccept(selectedInvitation)}
                  disabled={processing}
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accept Invitation'}
                </Button>
              </>
            )}
            {selectedInvitation?.status !== 'pending' && (
              <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Confirmation Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Decline Invitation</DialogTitle>
            <DialogDescription>
              Are you sure you want to decline this invitation from {selectedInvitation?.invited_by_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason (Optional)</Label>
            <Textarea
              placeholder="You can provide a reason for declining..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeclineDialog(false);
              setDeclineReason('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDecline}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Decline Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
